import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, fileUrl, inr, buildWaLink } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, Users, MessageCircle, Receipt, MapPin, Split } from "lucide-react";
import { SplitDialog } from "@/components/site/SplitDialog";

const PHASE = { upcoming: ["Upcoming", "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"], completed: ["Completed", "bg-primary/10 text-primary"], pending: ["Awaiting payment", "bg-amber-500/15 text-amber-700 dark:text-amber-400"], cancelled: ["Cancelled", "bg-muted text-muted-foreground"] };
const ORDER = ["upcoming", "pending", "completed", "cancelled"];

export default function MyBookings() {
  const [rows, setRows] = useState(null);
  const [splitFor, setSplitFor] = useState(null);
  const { settings } = useSettings();
  const load = () => api.get("/account/bookings").then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  if (!rows) return <p className="text-muted-foreground">Loading…</p>;
  if (!rows.length) return (
    <div className="rounded-3xl border border-dashed p-12 text-center" data-testid="bookings-empty">
      <MapPin className="h-10 w-10 mx-auto text-muted-foreground" /><p className="font-heading text-2xl mt-3">No bookings yet</p>
      <p className="text-sm text-muted-foreground mt-1">Bookings made with your WhatsApp number or email appear here automatically.</p>
      <Link to="/trips"><Button className="rounded-full mt-5">Find your next trip</Button></Link>
    </div>
  );
  const groups = ORDER.map((p) => [p, rows.filter((r) => r.phase === p)]).filter(([, l]) => l.length);
  return (
    <div className="space-y-10" data-testid="my-bookings">
      {groups.map(([phase, list]) => (
        <section key={phase}>
          <h2 className="font-heading text-2xl mb-4 capitalize">{PHASE[phase][0]} <span className="text-muted-foreground text-base">· {list.length}</span></h2>
          <div className="grid gap-4 md:grid-cols-2">
            {list.map((b, i) => (
              <motion.div key={b.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="rounded-2xl border bg-card overflow-hidden flex" data-testid="booking-card">
                {b.cover_image && <img src={fileUrl(b.cover_image)} alt="" className="w-28 sm:w-36 object-cover" />}
                <div className="p-4 flex-1 min-w-0 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0"><p className="font-heading text-xl leading-tight truncate">{b.trip_name}</p><p className="text-xs text-muted-foreground">{b.destination}{b.duration ? ` · ${b.duration}` : ""}</p></div>
                    <Badge variant="outline" className={`rounded-full shrink-0 ${PHASE[b.phase][1]}`}>{PHASE[b.phase][0]}</Badge>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> {b.departure_date}</span>
                    <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {b.travellers} traveller{b.travellers > 1 ? "s" : ""}</span>
                    {b.booking_id && <span className="font-mono text-primary">{b.booking_id}</span>}
                  </div>
                  <div className="flex items-end justify-between gap-2">
                    <div className="text-sm"><span className="text-muted-foreground">Total </span><b>{inr(b.final_total)}</b>{b.balance_due > 0 && b.phase !== "cancelled" && <span className="text-amber-600 dark:text-amber-400"> · due {inr(b.balance_due)}</span>}</div>
                    <div className="flex gap-1">
                      {b.phase === "upcoming" && b.balance_due > 0 && <Button size="sm" variant="outline" className="h-8 rounded-full text-xs" onClick={() => setSplitFor(b)} data-testid="booking-split"><Split className="h-3.5 w-3.5 mr-1" /> Split</Button>}
                      {b.invoice && <Link to={`/account/invoice/${b.invoice.id}`} title="Invoice"><Button size="icon" variant="ghost" className="h-8 w-8" data-testid="booking-invoice"><Receipt className="h-4 w-4" /></Button></Link>}
                      <a href={buildWaLink(settings.whatsapp_number, `Hi! Regarding my booking ${b.booking_id || ""} for ${b.trip_name} (${b.departure_date}).`)} target="_blank" rel="noreferrer"><Button size="icon" variant="ghost" className="h-8 w-8 text-[#1da851]"><MessageCircle className="h-4 w-4" /></Button></a>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      ))}
      <SplitDialog booking={splitFor} open={!!splitFor} onOpenChange={(o) => !o && setSplitFor(null)} onSaved={load} />
    </div>
  );
}
